import { Chart, ChartTypes } from "@uicore";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Typography,
} from "@altimateai/lego";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import usePaginate from "@/components/paginate/usePaginate";
import { useQuery } from "@tanstack/react-query";
import {
  getAccountsList,
  getAccountsTotalCost,
  getAccountsChartData,
  getAccountsTableData,
} from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";
import { DataTable } from "@/components/tables";
import { formatCost } from "@/helpers/utils";
import dayjs from "dayjs";
import { Bar } from "recharts";
import { createColorMap } from "@/pages/query/common";
import { FlexListSummary } from "@/components/listSummary";
import {
  GET_ACCOUNTS_LIST,
  GET_ACCOUNTS_TOTAL_COST,
  GET_ACCOUNTS_CHART_DATA,
  GET_ACCOUNTS_TABLE_DATA,
  AccountsCostQueryParams,
  TableData,
} from "@/types/accounts-cost";
import { IconChevronDown } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { CheckColoredIcon } from "@assets/icons";
import { Paginate } from "@/components/paginate";
import { debounce } from "lodash";
import { DateRange } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useBreadcrumb } from "@/components/breadcrumbs";

const USAGE_TYPES = ["compute", "storage", "serverless", "cloud", "ai_services", "others"];

const getDefaultGranularity = (startDate: Date, endDate: Date): "day" | "month" => {
  const diffInDays = dayjs(endDate).diff(dayjs(startDate), "day");

  if (diffInDays <= 28) return "day";
  return "month";
};

export const AccountsCost = () => {
  useBreadcrumb("Account Cost");
  const { currency } = useAppState();
  const { searchParams, deleteSearchParamsByKeyValue, updateSearchParamsByKey } =
    useFiltersBySearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate({ size: 20 });
  const [chartData, setChartData] = useState<Record<string, string | number>[]>([]);
  const [selectedLegends, setSelectedLegends] = useState<string[]>(USAGE_TYPES);
  const [summaryData, setSummaryData] = useState<{ label: string; value: string; info?: string }[]>(
    []
  );
  const [debouncedAccounts, setDebouncedAccounts] = useState<string[]>(
    searchParams.get("accounts")?.split(",") ?? []
  );
  const [accounts, setAccounts] = useState<string[]>(
    searchParams.get("accounts")?.split(",") ?? []
  );
  const [granularity, setGranularity] = useState<"day" | "month">(
    (searchParams.get("granularity") as "day" | "month") ?? "day"
  );
  // Store the full table data for client-side pagination
  const [fullTableData, setFullTableData] = useState<TableData[]>([]);
  const [open, setOpen] = useState(false);
  const [legendsOpen, setLegendsOpen] = useState(false);
  // Use the standard date hook like other working components
  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );
  const [lastDateRange, setLastDateRange] = useState({ startDate, endDate });

  // Reset granularity to default when date range actually changes
  useEffect(() => {
    const dateRangeChanged =
      lastDateRange.startDate.getTime() !== startDate.getTime() ||
      lastDateRange.endDate.getTime() !== endDate.getTime();

    if (dateRangeChanged) {
      const defaultGranularity = getDefaultGranularity(startDate, endDate);
      setGranularity(defaultGranularity);
      setLastDateRange({ startDate, endDate });

      // Update search params for granularity change
      if (defaultGranularity !== "day") {
        updateSearchParamsByKey({ granularity: defaultGranularity });
      } else {
        deleteSearchParamsByKeyValue({ granularity: undefined });
      }
    }
  }, [startDate, endDate, lastDateRange, updateSearchParamsByKey, deleteSearchParamsByKeyValue]);

  const getQueryParams = (
    includeDates = false,
    includeAccounts = false,
    includeGranularity = false
  ) => {
    const qp: AccountsCostQueryParams = {};
    if (includeDates) {
      qp["end_date"] = dayjs(endDate).format("YYYY-MM-DD");
      qp["start_date"] = dayjs(startDate).format("YYYY-MM-DD");
    }
    if (includeGranularity) {
      qp["aggregation_level"] = granularity;
    }
    if (accounts.length > 0 && includeAccounts) {
      qp["accounts"] = accounts;
    }
    return qp;
  };

  const { data: accountsList, isLoading: isAccountsListLoading } = useQuery({
    queryKey: [GET_ACCOUNTS_LIST],
    queryFn: () => getAccountsList(),
  });

  const { data: totalCostData, isLoading: isTotalCostLoading } = useQuery({
    queryKey: [GET_ACCOUNTS_TOTAL_COST, startDate, endDate, debouncedAccounts, granularity],
    queryFn: () => getAccountsTotalCost(getQueryParams(true, true, true)),
    enabled: !!accountsList,
  });

  const { data: chartDataResult, isLoading: isChartDataLoading } = useQuery({
    queryKey: [GET_ACCOUNTS_CHART_DATA, startDate, endDate, debouncedAccounts, granularity],
    queryFn: () => getAccountsChartData(getQueryParams(true, true, true)),
    enabled: !!accountsList,
  });

  // Handle successful chart data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (chartDataResult) {
      setChartData(
        chartDataResult.map(item => ({
          date: item.date,
          compute: item.compute || 0,
          storage: item.storage || 0,
          serverless: item.serverless || 0,
          cloud: item.cloud || 0,
          ai_services: item.ai_services || 0,
          others: item.others || 0,
        }))
      );
    }
  }, [chartDataResult]);

  const { data: tableDataResult, isLoading: isTableDataLoading } = useQuery({
    queryKey: [GET_ACCOUNTS_TABLE_DATA, startDate, endDate, debouncedAccounts, granularity],
    queryFn: () => getAccountsTableData(getQueryParams(true, true, true)),
    enabled: !!accountsList,
  });

  // Handle successful table data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (tableDataResult) {
      setFullTableData(tableDataResult);
      setPage(0); // Reset to first page when data changes
    }
  }, [tableDataResult, setPage]);

  const updateDebouncedAccounts = useCallback(
    debounce(newAccounts => {
      setDebouncedAccounts(newAccounts);
    }, 1500),
    []
  );

  const handleAccountChange = (account: string) => {
    setAccounts(prev => {
      const updatedAccounts = prev.includes(account)
        ? prev.filter(v => v !== account)
        : [...prev, account];
      updateDebouncedAccounts(updatedAccounts);

      // Update search params immediately
      if (updatedAccounts.length > 0) {
        updateSearchParamsByKey({ accounts: updatedAccounts.join(",") });
      } else {
        deleteSearchParamsByKeyValue({ accounts: undefined });
      }

      return updatedAccounts;
    });
  };

  const handleAccountsReset = () => {
    setAccounts([]);
    setDebouncedAccounts([]);
    deleteSearchParamsByKeyValue({ accounts: undefined });
  };

  const handleLegendsReset = () => {
    setSelectedLegends(USAGE_TYPES);
  };

  const handleLegendsChange = (legend: string) => {
    setSelectedLegends(prevState => {
      if (prevState.includes(legend)) {
        return prevState.filter(item => item !== legend);
      } else {
        return [...prevState, legend];
      }
    });
  };

  const handleGranularityChange = (value: "day" | "month") => {
    setGranularity(value);

    // Update search params immediately
    if (value !== "day") {
      updateSearchParamsByKey({ granularity: value });
    } else {
      deleteSearchParamsByKeyValue({ granularity: undefined });
    }
  };

  useEffect(() => {
    if (totalCostData) {
      setSummaryData([
        {
          label: "Total Cost",
          value: formatCost(totalCostData.cost ?? 0, currency),
        },
        {
          label: "Active Accounts",
          value: String(accounts.length > 0 ? accounts.length : (accountsList?.length ?? 0)),
        },
      ]);
    }
  }, [totalCostData, accounts, accountsList, granularity, currency]);

  const headers = [
    {
      label: "Account",
      key: "account_name",
      uid: "account_name",
      render: (item: TableData) => <span>{item.account_name}</span>,
      id: 0,
    },
    {
      label: "Total Cost",
      key: "total",
      uid: "total",
      render: (item: TableData) => <span>{formatCost(item.total, currency)}</span>,
      id: 1,
    },
    {
      label: "Compute",
      key: "compute",
      uid: "compute",
      render: (item: TableData) => <span>{formatCost(item.compute, currency)}</span>,
      id: 1,
    },
    {
      label: "Storage",
      key: "storage",
      uid: "storage",
      render: (item: TableData) => <span>{formatCost(item.storage, currency)}</span>,
      id: 2,
    },
    {
      label: "Serverless",
      key: "serverless",
      uid: "serverless",
      render: (item: TableData) => <span>{formatCost(item.serverless, currency)}</span>,
      id: 3,
    },
    {
      label: "Cloud",
      key: "cloud",
      uid: "cloud",
      render: (item: TableData) => <span>{formatCost(item.cloud, currency)}</span>,
      id: 4,
    },
    {
      label: "AI Services",
      key: "ai_services",
      uid: "ai_services",
      render: (item: TableData) => <span>{formatCost(item.ai_services, currency)}</span>,
      id: 5,
    },
    {
      label: "Others",
      key: "others",
      uid: "others",
      render: (item: TableData) => <span>{formatCost(item.others, currency)}</span>,
      id: 6,
    },
  ];

  const colorsMap = useMemo(() => {
    return createColorMap(USAGE_TYPES);
  }, []);

  // Calculate displayed data slice for client-side pagination
  const displayedTableData = useMemo(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    return fullTableData.slice(startIndex, endIndex);
  }, [fullTableData, page, pageSize]);

  return isAccountsListLoading ? (
    <>
      <Typography variant="page-heading">Accounts Cost</Typography>
      <RelativeComponentLoader label={"Loading..."} />
    </>
  ) : (
    <Stack direction={"column"} gap={"xl"}>
      <Stack justify={"between"}>
        <Typography variant={"h4"} weight={"semibold"}>
          Accounts Cost
        </Typography>
        <Stack align={"center"} gap={"xl"}>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Label className={"al-m-0"}>Accounts:</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  size={"sm"}
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  aria-label="Select accounts"
                  className="al-w-56 al-justify-between al-py-3 al-px-2 al-overflow-hidden al-text-ellipsis al-whitespace-nowrap"
                >
                  {accounts.length === 0
                    ? "All Accounts"
                    : accounts.join(", ").length > 22
                      ? `${accounts.join(", ").slice(0, 21)}...`
                      : accounts.join(", ")}
                  <IconChevronDown className="al-ml-2 al-h-4 al-w-4 al-shrink-0 al-opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="!al-w-56 !al-p-0">
                <Command>
                  <CommandInput placeholder="Search account..." className="h-9" />
                  <CommandList>
                    <CommandEmpty>No Account Found.</CommandEmpty>
                    <CommandGroup className={"al-overflow-auto"}>
                      {accountsList?.map(account => (
                        <CommandItem
                          key={account}
                          value={account}
                          onSelect={() => handleAccountChange(account)}
                          className={"al-cursor-pointer"}
                          title={account}
                        >
                          <CheckColoredIcon
                            className={cn(
                              "al-mr-2 al-size-4",
                              accounts.includes(account) ? "al-opacity-100" : "al-opacity-0"
                            )}
                          />
                          <span className="al-truncate">{account}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                  <div className={"al-border-t al-p-2"}>
                    <Button
                      size={"sm"}
                      variant={"outline"}
                      className={"!al-h-8 al-w-full"}
                      onClick={handleAccountsReset}
                    >
                      Clear Selection
                    </Button>
                  </div>
                </Command>
              </PopoverContent>
            </Popover>
          </Stack>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Label className={"al-m-0"}>Granularity:</Label>
            <Select value={granularity} onValueChange={handleGranularityChange}>
              <SelectTrigger className="al-w-32" aria-label="Select granularity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </Stack>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Label className={"al-m-0"}>Time Period:</Label>
            <DateRange
              startDate={startDate}
              endDate={endDate}
              availableStaticRanges={[
                "Last 7 days",
                "Last 28 days",
                "Last 3 months",
                "Last 6 months",
                "Last 12 months",
              ]}
              onDateRangeSelect={(newStartDate, newEndDate) => {
                // Only update search params - no local state needed
                updateSearchParamsByKey({
                  start_date: newStartDate?.toISOString(),
                  end_date: newEndDate?.toISOString(),
                });
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <FlexListSummary isLoading={isTotalCostLoading} summary={summaryData} />
      <Card>
        <CardHeader>
          <Stack align={"center"} justify={"between"}>
            <Stack direction={"column"} gap={"none"}>
              <Typography variant={"section-heading"}>Cost by Accounts and Usage Type</Typography>
              <Typography variant={"caption"} size={"sm"} className={"al-text-muted-foreground"}>
                {granularity === "day" ? "Daily" : "Monthly"} breakdown of costs by usage type
              </Typography>
            </Stack>
            <Stack direction={"row"} align={"center"} gap={"md"}>
              <Label className={"al-m-0"}>Usage Types:</Label>
              <Popover open={legendsOpen} onOpenChange={setLegendsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    size={"sm"}
                    variant="outline"
                    role="combobox"
                    aria-expanded={legendsOpen}
                    aria-label="Select usage types"
                    className="al-w-56 al-justify-between al-py-3 al-px-2 al-overflow-hidden al-text-ellipsis al-whitespace-nowrap"
                  >
                    {selectedLegends.length === 0
                      ? "All Usage Types"
                      : selectedLegends.join(", ").length > 18
                        ? `${selectedLegends.join(", ").slice(0, 18)}...`
                        : selectedLegends.join(", ")}
                    <IconChevronDown className="al-ml-2 al-h-4 al-w-4 al-shrink-0 al-opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="!al-w-56 !al-p-0">
                  <Command>
                    <CommandList>
                      <CommandGroup className={"al-overflow-auto"}>
                        {USAGE_TYPES.map(type => (
                          <CommandItem
                            key={type}
                            value={type}
                            onSelect={() => handleLegendsChange(type)}
                            className={"al-cursor-pointer"}
                            title={type.replace("_", " ").toUpperCase()}
                          >
                            <CheckColoredIcon
                              className={cn(
                                "al-mr-2 al-size-4",
                                selectedLegends.includes(type) ? "al-opacity-100" : "al-opacity-0"
                              )}
                            />
                            <span className="al-truncate">
                              {type.replace("_", " ").toUpperCase()}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                    <div className={"al-border-t al-p-2"}>
                      <Button
                        size={"sm"}
                        variant={"outline"}
                        className={"!al-h-8 al-w-full"}
                        onClick={handleLegendsReset}
                      >
                        Reset
                      </Button>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
            </Stack>
          </Stack>
        </CardHeader>
        <CardContent className={"al-pb-10"}>
          {isChartDataLoading ? (
            <RelativeComponentLoader componentHeight={30} label={"Loading Chart"} />
          ) : (
            <Chart
              title="Accounts Cost Overview"
              xAxisDataKey="date"
              xAxisLabelFormatter={value =>
                granularity === "day"
                  ? dayjs(value).format("DD MMM")
                  : dayjs(value).format("MMM YYYY")
              }
              yAxisLabelFormatter={value => formatCost(value, currency)}
              type={ChartTypes.BarChart}
              data={chartData}
              chartProps={{}}
              tooltipProps={{
                content: ({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) {
                    return null;
                  }

                  const totalCost = payload.reduce((acc, item) => acc + (item.value as number), 0);

                  return (
                    <div className="al-bg-white al-border al-p-2 al-rounded-md">
                      <div className="al-font-semibold al-mb-2">
                        {granularity === "day"
                          ? dayjs(label).format("DD MMM")
                          : dayjs(label).format("MMM YYYY")}
                      </div>
                      {payload.map(item => (
                        <Stack justify={"between"} align={"center"} gap={"md"} key={item.name}>
                          <Stack align={"center"}>
                            <span
                              className="al-w-3 al-h-3 al-rounded-full al-mr-2"
                              style={{ backgroundColor: item.color }}
                            ></span>
                            {(item.name as string).replace("_", " ").toUpperCase()}:
                          </Stack>
                          <span>{formatCost(item.value, currency)}</span>
                        </Stack>
                      ))}
                      {payload.length !== 1 && (
                        <Stack
                          justify={"between"}
                          className="al-border-t al-border-black al-pt-1 al-mt-1"
                        >
                          <span>Total:</span>
                          <span>{formatCost(totalCost, currency)}</span>
                        </Stack>
                      )}
                    </div>
                  );
                },
              }}
            >
              {({ selectedLegend }) => {
                const numberOfPoints = chartData.length;
                const barSize = Math.max(20, Math.min(120, 600 / numberOfPoints));

                return (
                  <>
                    {selectedLegends.length > 0
                      ? selectedLegends.map((type, i) => {
                          return (
                            <Bar
                              key={type}
                              barSize={barSize}
                              dataKey={type}
                              name={type}
                              fill={colorsMap[type]}
                              stackId="cost"
                              radius={
                                i === selectedLegends.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]
                              }
                              hide={Boolean(selectedLegend && selectedLegend !== type)}
                            />
                          );
                        })
                      : USAGE_TYPES.map((type, i) => {
                          return (
                            <Bar
                              key={type}
                              barSize={barSize}
                              dataKey={type}
                              name={type}
                              fill={colorsMap[type]}
                              stackId="cost"
                              radius={i === USAGE_TYPES.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]}
                              hide={Boolean(selectedLegend && selectedLegend !== type)}
                            />
                          );
                        })}
                  </>
                );
              }}
            </Chart>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Stack justify={"between"}>
            <Typography variant={"section-heading"}>Accounts Cost Details</Typography>
          </Stack>
        </CardHeader>
        <CardContent>
          {isTableDataLoading ? (
            <RelativeComponentLoader componentHeight={30} label={"Loading accounts cost details"} />
          ) : (
            <>
              <DataTable
                header={headers}
                onRowClick={() => {}}
                data={{ items: displayedTableData }}
                resetPage={() => {}}
              />
              <Paginate
                itemCount={fullTableData.length}
                numPages={Math.ceil(fullTableData.length / pageSize)}
                page={page}
                pageSize={pageSize}
                onPageClick={setPage}
                onPageSizeChange={updatePageSize}
                showPageSize
              />
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};
